<template>
  <div>
    <!-- Quick filter to changed assigned to filter (me / none / all) -->
    <QuickFilterView
      :quick-filter="quickFilter"
      @quick-filter-changed="quickFilterChanged"
    ></QuickFilterView>

    <!-- Filters -->
    <TableFilterView
      :columns="columns"
      :initial-filters="activeFilters"
      :show-export="false"
      @update-active-filters="updateActiveFilters"
      @update-visible-columns="updateVisibleColumns"
    />
    <!--Table-->

    <b-table
      :ref="tableRef"
      class="harmonisation-table"
      :striped="false"
      :hoverable="false"
      :debounce-search="config.searchDebounce"
      :aria-next-label="config.nextLabel()"
      :aria-previous-label="config.previousLabel()"
      :aria-page-label="config.pageLabel()"
      :aria-current-label="config.currentPageLabel()"
      :per-page="config.perPage"
      :sticky-header="config.stickyHeader"
      :current-page="currentPagedSortedParameter.pageNumber"
      :pagination-position="config.paginationPosition"
      paginated
      backend-pagination
      :sort-icon="config.sortIcon"
      :sort-icon-size="config.sortIconSize"
      backend-sorting
      sort-multiple
      :sort-multiple-data="buefySortingPriority"
      :total="total"
      :data="projects"
      :loading="isLoading"
      @sort="onSort"
      @sorting-priority-removed="sortingPriorityRemoved"
      @page-change="onPageChange"
    >
      <b-table-column
        v-for="column in columns"
        :key="column.field"
        v-bind="column"
        :visible="column.visible"
        :cell-class="
          column.field == 'evaluation.instructeur' ? 'harmonisation-cell' : 'standard-cell'
        "
        :width="
          column.field == 'evaluation.instructeur'
            ? 210
            : column.field == 'evaluation.mention'
              ? 110
              : column.field == 'evaluation.commentaires'
                ? 50
                : column.field.indexOf('evaluation.') > -1
                  ? 80
                  : 'auto'
        "
      >
        <template v-if="column.field.indexOf('evaluation.') > -1" #header="{ column: col }">
          {{ col.label }}
        </template>
        <template #default="props">
          <div>
            <!-- default fields -->
            <div v-if="column.field === 'etablissement.codeEtablissement'">
              <div v-if="props.row['etablissements']">
                <div
                  v-for="organisme in props.row['etablissements'].filter(
                    (o: any) => o && o.id && o.nom,
                  )"
                  :key="props.row['id'] + '_' + organisme.codeEtablissement"
                >
                  {{ organisme.codeEtablissement }}
                </div>
              </div>
            </div>
            <div
              v-if="column.field === 'etablissement.name'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              <div v-if="props.row['etablissements']">
                <div
                  v-for="organisme in props.row['etablissements'].filter(
                    (o: any) => o && o.id && o.nom,
                  )"
                  :key="organisme.id"
                >
                  {{ organisme.nom }}
                </div>
              </div>
            </div>
            <div
              v-else-if="column.field === 'projet.nom'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              {{ props.row["nom"] }}
            </div>
            <div
              v-else-if="column.field === 'typeDossier'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              {{ $t(props.row.typeDossier) }}
            </div>
            <div
              v-else-if="column.field === 'statut'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              <StatutDossierTag
                v-if="loggedUser"
                :dossier="props.row"
                can-see-instruction-comment
                :logged-user="loggedUser"
              />
            </div>
            <div
              v-if="column.field === 'enseignant.name'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              <div
                v-for="(utilisateur, i) in props.row['enseignants'].filter(
                  (u: any) => u && u.id && u.nom,
                )"
                :key="i + '_' + utilisateur.id"
              >
                {{ utilisateur.prenom + " " + utilisateur.nom }}
              </div>
            </div>
            <div
              v-if="column.field === 'referent.name'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              <div
                v-for="(utilisateur, i) in props.row['referents'].filter(
                  (u: any) => u && u.id && u.nom,
                )"
                :key="i + '_' + utilisateur.id"
              >
                {{ utilisateur.prenom + " " + utilisateur.nom }}
              </div>
            </div>
            <div
              v-else-if="column.field == 'evaluation.instructeur'"
              class="instructeur-cell-column"
            >
              <LabellisationHarmonisationForProjectView
                v-if="loggedUser"
                :project-annuel="props.row"
                :evaluations="props.row['evaluations']"
                :logged-user="loggedUser"
                :all-grae-instructeurs="allGraeInstructeurs"
                @evaluations-changed="evaluationChanged(props.row, $event)"
                @reload-data="reloadData()"
              />
            </div>
            <div
              v-else-if="column.field.indexOf('evaluation.mention') > -1"
              class="mention-cell"
            ></div>
            <div
              v-else-if="column.field.indexOf('evaluation.commentaires') > -1"
              class="comment-cell"
            ></div>
            <div v-else-if="column.field.indexOf('evaluation.') > -1" class="note-cell">
              <!-- Hidden, will be displayed by 'evaluation.instructeur' column -->
            </div>
            <div
              v-else-if="column.field === 'dateModification'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              {{ formatDateTime(props.row[column.field]) }}
            </div>
            <div v-if="column.field === 'natureAireEducative'">
              {{ props.row["natureAireEducative"] }}
            </div>

            <!-- hidden fields -->
            <div
              v-if="column.field === 'codeDepartement'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              <div v-if="props.row['etablissements'].length">
                {{ props.row["etablissements"][0].codeDepartement.replace(/^0+/, "") }}
              </div>
            </div>
            <div
              v-if="column.field === 'ville'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              <div v-if="props.row['etablissements'].length">
                {{ props.row["etablissements"][0].ville }}
              </div>
            </div>
            <div
              v-if="column.field === 'structure.name'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              <div v-if="props.row['structures']">
                <div
                  v-for="organisme in props.row['structures'].filter(
                    (o: any) => o && o.id && o.nom,
                  )"
                  :key="organisme.id"
                >
                  {{ organisme.nom }}
                </div>
              </div>
            </div>

            <!-- non customised fields -->
            <div
              v-if="!column.custom"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              <div>{{ props.row[column.field] }}</div>
            </div>
          </div>
        </template>
      </b-table-column>
    </b-table>
  </div>
</template>

<script lang="ts">
import {
  EvaluationDTO,
  FilterDTO,
  PaginationOrder,
  ProjetAnnuelDTO,
  ProjetAnnuelForInstructionDTO,
  QuickFilter,
  UtilisateurDTO,
} from "@/model/bean/GeneratedDTOs"
import { Filters } from "@/model/bean/Filters"
import RouterUtils from "@/router/RouterUtils"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import { GraeService } from "@/services/rest/grae/GraeService"
import { LoginService } from "@/services/rest/login/LoginService"
import { defineComponent } from "vue"
import Column from "@/views/tables/Column"
import QuickFilterView from "../QuickFilterView.vue"
import TableFilterView from "@/views/tables/TableFilterView.vue"
import StatutDossierTag from "@/views/dossier/StatutDossierTag.vue"
import LabellisationHarmonisationForProjectView from "./LabellisationHarmonisationForProjectView.vue"
import { InstructionService } from "@/services/rest/instruction/InstructionService"
import { EnumHelper } from "@/model/bean/EnumHelper"
import { useTableView } from "@/hooks/useTableView"
import { BTable, BTableColumn } from "buefy"
import DateUtils from "@/services/DateUtils"
import { SortOrder } from "@/model/SortOrder"

export default defineComponent({
  name: "LabellisationHarmonisationView",
  components: {
    QuickFilterView,
    TableFilterView,
    StatutDossierTag,
    LabellisationHarmonisationForProjectView,
    BTable,
    BTableColumn,
  },
  props: {
    graeId: {
      type: String,
      required: true,
    },
    activeQuickFilter: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      // Propriétés spécifiques à cette classe
      quickFilter: "NOT_ASSIGNED" as QuickFilter | undefined,
      loginService: LoginService.INSTANCE,
      graeService: GraeService.INSTANCE,
      instructionService: InstructionService.INSTANCE,
      infoReportingService: InfoReportingService.INSTANCE,
      allGraeInstructeurs: [] as UtilisateurDTO[],

      loggedUser: new UtilisateurDTO() as UtilisateurDTO | undefined,
      projects: [] as Array<ProjetAnnuelDTO>,

      // Hook initialized in created()
      tableView: null as any,
    }
  },
  computed: {
    // Propriétés réactives du tableau
    tableRef(): string {
      return this.tableView?.state.tableRef || ""
    },
    isLoading: {
      get(): boolean {
        return this.tableView?.state.isLoading || false
      },
      set(value: boolean): void {
        if (this.tableView) {
          this.tableView.state.isLoading = value
        }
      },
    },
    columns(): Array<Column> {
      return this.tableView?.state.columns || []
    },
    currentPagedSortedParameter(): any {
      return this.tableView?.state.currentPagedSortedParameter
    },
    buefySortingPriority(): Array<any> {
      return this.tableView?.state.buefySortingPriority || []
    },
    total: {
      get(): number {
        return this.tableView?.state.total || 0
      },
      set(value: number): void {
        if (this.tableView) {
          this.tableView.state.total = value
        }
      },
    },
    config(): any {
      return this.tableView?.state.config
    },
    activeFilters(): Array<FilterDTO> {
      return this.tableView?.state.activeFilters || []
    },
  },
  watch: {
    activeQuickFilter(): void {
      this.activeQuickFilterChanged()
    },
  },
  created(): void {
    // Initialize hook with proper binding
    this.tableView = useTableView({
      tableRef: "harmonisation-table",
      router: this.$router,
      route: this.$route,
      i18n: this.$i18n,
      getInitialColumns: this.getInitialColumns.bind(this),
      getDefaultSort: this.getDefaultSort.bind(this),
      getInitialActiveFilters: this.getInitialActiveFilters.bind(this),
      reloadData: this.reloadData.bind(this),
      reloadDataWhenMounted: true,
    })

    this.loggedUser = this.loginService.getLoggedUser()
    if (!this.loggedUser || !this.loggedUser.id) {
      return
    }
    this.activeQuickFilterChanged()
  },
  async mounted(): Promise<void> {
    this.tableView?.initMounted()
    await this.loadAllInstructeurs()
  },
  methods: {
    // Méthodes du hook useTableView
    updateActiveFilters(activeFilters: Array<FilterDTO>): void {
      this.tableView?.updateActiveFilters(activeFilters)
    },

    updateVisibleColumns(columns: Array<Column>): void {
      this.tableView?.updateVisibleColumns(columns)
    },

    onPageChange(newPage: string | number): void {
      const pageNum = typeof newPage === "string" ? parseInt(newPage) : newPage
      this.tableView.onPageChange(pageNum)
    },

    onSort(field: string | undefined, order: SortOrder): void {
      if (field) {
        this.tableView.onSort(field, order)
      }
    },

    sortingPriorityRemoved(field: string | undefined): void {
      if (field) {
        this.tableView.sortingPriorityRemoved(field)
      }
    },

    getInitialActiveFilters(): Array<FilterDTO> {
      return new Array<FilterDTO>()
    },

    updateTotal(total: number): void {
      this.tableView?.updateTotal(total)
    },

    activeQuickFilterChanged(): void {
      if (this.activeQuickFilter == 0) {
        this.quickFilter = "MY_ASSIGNMENTS"
      } else if (this.activeQuickFilter == 1) {
        this.quickFilter = "NOT_ASSIGNED"
      } else {
        this.quickFilter = undefined
      }
    },

    async loadAllInstructeurs(): Promise<void> {
      const allInstructeursNotSorted = await this.graeService.getInstructeurs(this.graeId)
      this.allGraeInstructeurs = [
        ...allInstructeursNotSorted.filter((instructeur) => instructeur.id === this.loggedUser?.id),
        ...allInstructeursNotSorted.filter((instructeur) => instructeur.id !== this.loggedUser?.id),
      ]
    },

    quickFilterChanged(newQuickFilter: QuickFilter): void {
      this.quickFilter = newQuickFilter
      const params = this.$router.currentRoute.params || {}
      let tabIndex
      if (this.quickFilter === "MY_ASSIGNMENTS") {
        tabIndex = 0
      } else if (this.quickFilter === "NOT_ASSIGNED") {
        tabIndex = 1
      } else {
        tabIndex = 2
      }
      const currentActiveQuickFilter = params["activeQuickFilter"] || "0"
      if (tabIndex.toString() !== currentActiveQuickFilter) {
        params["activeQuickFilter"] = tabIndex.toString()
        this.$router.push({ params: params })
      }
      this.tableView?.reload()
    },

    async reloadData(): Promise<void> {
      const statutFilter = Filters.asFilter({
        property: "statut",
        values: ["EN_ATTENTE", "INSTRUCTION_EN_COURS"],
      })
      const onlyNotValidatedFilter = Filters.asFilter({
        property: "evaluationFinaleValidated",
        values: ["false"],
      })
      try {
        // TODO Should be harmonisation projects
        const paginatedProjectsList = await this.instructionService.getLabellisations(
          this.graeId,
          this.currentPagedSortedParameter,
          [...this.activeFilters, statutFilter, onlyNotValidatedFilter],
          this.quickFilter,
        )
        this.projects = paginatedProjectsList.elements
        this.updateTotal(paginatedProjectsList.count)
      } catch (e: any) {
        // Silent catch
      }
    },

    getInitialColumns(): Array<Column> {
      return [
        {
          field: "codeDepartement",
          label: this.$t("graePage.details.projets.dpt").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "ville",
          label: this.$t("graePage.details.projets.ville").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "etablissement.name",
          label: this.$t("graePage.details.projets.etablissements").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "natureAireEducative",
          label: this.$t("graePage.details.projets.nature-ae").toString(),
          sortable: true,
          backEndSearchable: true,
          possibleSearchValues: EnumHelper.allNatures,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "etablissement.codeEtablissement",
          label: this.$t("graePage.details.projets.uais").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "projet.nom",
          label: this.$t("graePage.details.projets.nom").toString(),
          sortable: true,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "typeDossier",
          label: this.$t("graePage.details.projets.typeDossier").toString(),
          sortable: true,
          backEndSearchable: true,
          possibleSearchValues: EnumHelper.allTypeDossiers,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "enseignant.name",
          label: this.$t("graePage.details.projets.enseignants").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "structure.name",
          label: this.$t("graePage.details.projets.structures").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "statut",
          label: this.$t("graePage.details.projets.statut").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "dateModification",
          label: this.$t("graePage.details.projets.dateModification").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "referent.name",
          label: this.$t("graePage.details.projets.referents").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "evaluation.instructeur",
          label: this.$t("graePage.details.projets.instructeur").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "evaluation.deroule",
          label: this.$t("instructionPage.labellisations.harmonisation.note.deroule").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },

        {
          field: "evaluation.diffusion",
          label: this.$t("instructionPage.labellisations.harmonisation.note.diffusion").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "evaluation.enseignements",
          label: this.$t(
            "instructionPage.labellisations.harmonisation.note.enseignements",
          ).toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "evaluation.implication",
          label: this.$t(
            "instructionPage.labellisations.harmonisation.note.implication",
          ).toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "evaluation.commentaires",
          label: this.$t(
            "instructionPage.labellisations.harmonisation.note.commentaires",
          ).toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "evaluation.mention",
          label: this.$t("instructionPage.labellisations.harmonisation.note.mention").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "evaluation.actions",
          label: this.$t("instructionPage.labellisations.harmonisation.actions").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
      ]
    },

    getDefaultSort(): Array<PaginationOrder> {
      const defaultSort = new PaginationOrder()
      defaultSort.clause = "projet.nom"
      defaultSort.desc = false
      return [defaultSort]
    },

    showProjectDetailsPage(event: Event, projetId: string, projetAnnuelId: string): void {
      // Do not foward click event to row (would trigger modal)
      event.stopPropagation()
      this.$router.push(RouterUtils.getRouteForProjectDetails(projetId, projetAnnuelId))
    },

    formatDateTime(date: Date): string {
      return DateUtils.formatDateTime(date)
    },

    evaluationChanged(
      projetAnnuel: ProjetAnnuelForInstructionDTO,
      newEvaluations: ProjetAnnuelForInstructionDTO,
    ): void {
      console.log("Evaluation changed for project " + projetAnnuel, newEvaluations)
      // projetAnnuel = newEvaluations
      this.reloadData()
    },
  },
})
</script>

<style lang="scss">
@import "@/styles/variables";

.harmonisation-cell {
  padding: 0 0 !important;
  border-bottom: 2px solid $instruction-harmonisation-final-eval-border-big !important;
}

.standard-cell {
  border-bottom: 2px solid $instruction-harmonisation-final-eval-border-big !important;
}

.note-cell {
  width: 85px;
  font-size: 14px;
}

.comment-cell {
  width: 50px;
  font-size: 14px;
}

.mention-cell {
  width: 120px;
  font-size: 14px;
}

.instructeur-cell-column {
  width: 220px;
}

.harmonisation-table {
  tr {
    &:last-child {
      .harmonisation-cell {
        padding: 0 0 !important;
        border-bottom: 2px solid $instruction-harmonisation-final-eval-border-big !important;

        .project-harmonisation-container {
          .project-harmonisation-table {
            tr {
              &:last-child {
                height: 50px;
              }
            }
          }
        }
      }
    }
  }
}
</style>
